import { Grid } from '@mui/material';
import { HexColor } from '@nx-smartmonkey/shared/domain';
import { validatePhoneNumber } from '@nx-smartmonkey/shared/helpers';
import { VSpacer } from '@nx-smartmonkey/ui';
import { useEffect } from 'react';

import { NewVehiclePill } from '../../../../components/create-vehicle/NewVehiclePill';
import { Organization } from '../../../../domain/organization/Organization';
import { Project } from '../../../../domain/project/Project';
import { Vehicle } from '../../../../domain/vehicle/Vehicle';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';

export const createVehicle = (vehicles: Vehicle[], project: Project, organizationId: string) =>
  Vehicle.create(
    {
      organization_id: organizationId,
      project_id: project!.id,
      label: ``,
      default_start_location: undefined,
      default_end_location: undefined,
      default_time_window: Vehicle.getDefaultTimeWindow(),
      color: HexColor.create(Vehicle.getDefaultColor(vehicles)),
      ...(project!.constraints.volume && { default_max_volume: 0 }),
      ...(project!.constraints.weight && { default_max_weight: 0 }),
      enabled: true,
    },
    `id-${Math.random()}`
  );

export const isVehicleValid = (vehicle: Vehicle) =>
  vehicle.label !== `` && (!vehicle.phone || validatePhoneNumber(vehicle.phone));

type NewProjectVehiclesProps = {
  activeProject: Project;
  organization: Organization;
  vehicles: Vehicle[];
  setVehicles: (vehicles: Vehicle[]) => void;
};

export const NewProjectVehicles = ({ activeProject, organization, vehicles, setVehicles }: NewProjectVehiclesProps) => {
  useEffect(() => {
    if (activeProject && vehicles.length === 0) {
      setVehicles([createVehicle(vehicles, activeProject, organization.id)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container direction="column" wrap="nowrap" style={{ maxWidth: `740px`, padding: `0 20px` }}>
      <Grid item container direction="row" wrap="nowrap" alignItems="center" justifyContent="space-between">
        <Grid item>
          <Text variant="h6" intlId="projects.create.create_vehicles" />
        </Grid>
        <Grid item>
          <Button
            variant="flat"
            intlId="projects.create.create_vehicles.add"
            onClick={() => {
              setVehicles([...vehicles, createVehicle(vehicles, activeProject, organization.id)]);
            }}
          />
        </Grid>
      </Grid>

      <VSpacer large />

      <Grid container style={{ height: `100%`, maxHeight: `calc(100vh - 240px)`, overflowY: `auto` }}>
        <Grid item container direction="column" wrap="nowrap" alignItems="center">
          {vehicles.map((vehicle, index) => (
            <Grid item key={vehicle.id} style={{ width: `100%`, maxWidth: `650px` }}>
              <NewVehiclePill
                project={activeProject}
                organization={organization}
                vehicle={vehicle}
                constraints={{
                  volume: activeProject.constraints.volume,
                  weight: activeProject.constraints.weight,
                }}
                onUpdate={(updatedVehicle: Vehicle) => {
                  const updatedVehicles = vehicles.map((v) => {
                    if (v.id === updatedVehicle.id) {
                      return updatedVehicle;
                    }
                    return v;
                  });
                  setVehicles(updatedVehicles);
                }}
                canRemove={vehicles.length > 1}
                onRemove={(vehicleToRemove: Vehicle) => {
                  const updatedVehicles = vehicles.filter((v) => v.id !== vehicleToRemove.id);
                  setVehicles(updatedVehicles);
                }}
              />

              {index < vehicles.length - 1 && <VSpacer medium />}
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};
